/**
 * "Show sent" — what went out, fetched ONLY while the user asked to see it, ONLY for the
 * range on screen, and never twice for the same range.
 *
 * The product owner's rule: "fetch only when the user wants to see them". So nothing here
 * runs with "Show sent" off, and turning it on costs exactly the queries the visible range
 * needs, for the kinds that are switched on:
 *
 *   messages / Statuses → ONE user-messages query: the range's sent records. Soft-deleted
 *                         rows INCLUDED (a delivered send is `deleted: true` — see
 *                         `isSentRecord`), campaign sends EXCLUDED at the server
 *                         (`campaignId $exists false`): their campaign stands for them, so
 *                         the ~700 per-recipient rows of a busy month are never requested.
 *   campaigns           → the range's completed campaigns: one query for standalone
 *                         campaigns and one for drip steps (the backend hides drip steps from
 *                         any find that does not ask for them — `onlyDripSteps`).
 *
 * WHERE THE RESULTS LIVE — deliberately apart for campaigns:
 *   - sent messages go into `DBUserMessageReducer`, where the sidebar rows look them up.
 *     Every other reader of that slice already excludes `deleted: true`, so a sent record is
 *     invisible to everything but the planner predicate that asks for it by rule.
 *   - completed campaigns live HERE, not in `DBCampaignReducer`: that slice is what the send
 *     tick scans, its `total` drives the campaigns list's pagination, and the calendar's
 *     duplicate-chip guards would read a completed step as "this drip is covered" and
 *     suppress its real upcoming steps. Their own slice, their own calendar pass.
 *
 * NOT PERSISTED. Neither is the toggle: a reload starts with "Show sent" off and this slice
 * empty ("so it doesn't weigh on anything").
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Campaign } from '@common/models/campaign-model';
import { DBUserMessageThunks } from '@common-reducers/DBServiceThunks';
import { findEntity } from '@common-reducers/backend-api/backend-api';
import {
    normalizePlannerFilters,
    SENT_CAMPAIGN_STATUSES,
    SENT_RECORD_STATUSES,
    type PlannerFilters,
} from '@common/services/planner-filters';

export type SentHistoryGroup = 'messages' | 'campaigns';

/** A closed time range, epoch ms. */
export interface SentRange { start: number; end: number }

export interface PlannerSentHistoryState {
    /** Completed campaigns (standalone + drip steps) of every range loaded, by id. */
    campaigns: Record<string, Campaign>;
    /** Ranges already fetched, per group — a range inside one of these is not fetched again. */
    loaded: Record<SentHistoryGroup, SentRange[]>;
    /** Ranges being fetched right now — a second request for them waits instead of repeating. */
    inFlight: Record<SentHistoryGroup, SentRange[]>;
    /** Ranges whose last fetch FAILED. Not loaded, so the next request retries them; kept so
     *  the calendar can say the picture may be incomplete instead of spinning or going quiet. */
    failed: Record<SentHistoryGroup, SentRange[]>;
}

const initialState: PlannerSentHistoryState = {
    campaigns: {},
    loaded: { messages: [], campaigns: [] },
    inFlight: { messages: [], campaigns: [] },
    failed: { messages: [], campaigns: [] },
};

/** Is `range` inside one of `ranges`? */
export function isRangeCovered(ranges: ReadonlyArray<SentRange>, range: SentRange): boolean {
    return ranges.some(r => r.start <= range.start && r.end >= range.end);
}

/** The groups whose kinds are switched on — what "Show sent" has to load for them. */
export function wantedSentHistoryGroups(rawFilters: PlannerFilters | unknown): SentHistoryGroup[] {
    const f = normalizePlannerFilters(rawFilters);
    if (!f.showSent) return [];
    const wanted: SentHistoryGroup[] = [];
    if (f.showMessages || f.showStatuses) wanted.push('messages');
    if (f.showCampaigns && !f.groupsOnly) wanted.push('campaigns');
    return wanted;
}

/**
 * Is what went out in `range` still on its way, or did it fail to arrive? What the loading
 * indicators read — the REAL requests, never a timer:
 *   - `loading`: a fetch that covers the range on screen is in flight, for a kind that is on.
 *     A slow response for a month the user already left does not count: it covers THAT
 *     month, not this one (its rows still land in the cache, each placed by its own date, so
 *     it can never paint over the month on screen).
 *   - `failed`: nothing is loading, the range is not loaded, and its last fetch failed —
 *     the data on screen may be incomplete, and a retry is one request away.
 */
export function sentHistoryStatus(
    rawFilters: PlannerFilters | unknown,
    range: SentRange,
    state: Pick<PlannerSentHistoryState, 'loaded' | 'inFlight' | 'failed'> | undefined,
): { loading: boolean; failed: boolean } {
    if (!state) return { loading: false, failed: false };
    const groups = wantedSentHistoryGroups(rawFilters);
    const loading = groups.some(g => isRangeCovered(state.inFlight[g], range));
    const failed = !loading && groups.some(g =>
        !isRangeCovered(state.loaded[g], range) && state.failed[g].some(r => r.start <= range.end && r.end >= range.start));
    return { loading, failed };
}

/**
 * Which groups a range still needs, for a filter state. Pure — the whole fetch policy:
 *   - "Show sent" off → nothing, whatever else is on;
 *   - a group whose kinds are all off → nothing for it;
 *   - a range already loaded or in flight for the group → nothing for it.
 */
export function planSentHistoryFetch(
    rawFilters: PlannerFilters | unknown,
    range: SentRange,
    state: Pick<PlannerSentHistoryState, 'loaded' | 'inFlight'>,
): SentHistoryGroup[] {
    return wantedSentHistoryGroups(rawFilters)
        .filter(g => !isRangeCovered(state.loaded[g], range) && !isRangeCovered(state.inFlight[g], range));
}

/** The user-messages query for a range's sent records (see the header). */
export function sentMessagesQuery(range: SentRange) {
    return {
        status: { $in: [...SENT_RECORD_STATUSES] },
        dueDate: { $gte: new Date(range.start).toISOString(), $lte: new Date(range.end).toISOString() },
        campaignId: { $exists: false },
        $sort: { dueDate: 1 },
        // The range bounds the result; there is no row cap to fall off (the old 600-row cap
        // was already exceeded by a single month on a real account).
        $limit: -1,
    };
}

/** The campaigns query for a range's completed campaigns; `dripSteps` asks for the other partition. */
export function sentCampaignsQuery(range: SentRange, dripSteps: boolean) {
    return {
        'schedule.dueDate': { $gte: new Date(range.start).toISOString(), $lte: new Date(range.end).toISOString() },
        campaignStatus: { $in: [...SENT_CAMPAIGN_STATUSES] },
        $limit: -1,
        ...(dripSteps ? { onlyDripSteps: true } : {}),
    };
}

const slice = createSlice({
    name: 'PlannerSentHistory',
    initialState,
    reducers: {
        started: (state, { payload }: PayloadAction<{ group: SentHistoryGroup; range: SentRange }>) => {
            state.inFlight[payload.group].push(payload.range);
            // A retry clears the failure it retries.
            state.failed[payload.group] = state.failed[payload.group]
                .filter(r => !(r.start >= payload.range.start && r.end <= payload.range.end));
        },
        // Every started fetch settles exactly once — success, error, or a response that never
        // parsed — so no indicator can be left spinning.
        settled: (state, { payload }: PayloadAction<{ group: SentHistoryGroup; range: SentRange; ok: boolean }>) => {
            const i = state.inFlight[payload.group].findIndex(r => r.start === payload.range.start && r.end === payload.range.end);
            if (i >= 0) state.inFlight[payload.group].splice(i, 1);
            if (payload.ok) state.loaded[payload.group].push(payload.range);
            else state.failed[payload.group].push(payload.range);
        },
        campaignsLoaded: (state, { payload }: PayloadAction<Campaign[]>) => {
            for (const c of payload) {
                if (c?._id) state.campaigns[String(c._id)] = c as any;
            }
        },
    },
});

export const plannerSentHistoryActions = slice.actions;
export default slice.reducer;

/** Fetch a campaigns partition WITHOUT touching DBCampaignReducer (see the header). */
async function fetchCampaigns(dispatch: any, query: Record<string, unknown>): Promise<Campaign[]> {
    // The same $in → `field[$in][]` shape the DB thunks send.
    const transformed: Record<string, unknown> = { ...query };
    for (const [key, value] of Object.entries(query)) {
        if (value && typeof value === 'object' && '$in' in (value as object)) {
            delete transformed[key];
            transformed[`${key}[$in][]`] = (value as { $in: unknown[] }).$in;
        }
    }
    const sub = dispatch(findEntity.initiate({ service: 'campaigns', query: transformed }, { forceRefetch: true }));
    try {
        const response = await sub.unwrap();
        const data = Array.isArray(response) ? response : (response as any)?.data;
        return Array.isArray(data) ? data : [];
    } finally {
        sub.unsubscribe?.();
    }
}

/**
 * Load what went out in `range` for the kinds that are on — or nothing at all, when
 * "Show sent" is off or the range is already covered. Safe to dispatch on every render
 * that changes the range or the filters: the plan decides, not the caller.
 */
export const loadSentHistoryForRange = createAsyncThunk<
    SentHistoryGroup[],
    { start: string | Date; end: string | Date },
    { state: any }
>('plannerSentHistory/loadRange', async ({ start, end }, { dispatch, getState }) => {
    const range: SentRange = { start: new Date(start).getTime(), end: new Date(end).getTime() };
    if (Number.isNaN(range.start) || Number.isNaN(range.end)) return [];
    const state = getState();
    const history: PlannerSentHistoryState | undefined = state.PlannerSentHistoryReducer;
    if (!history) return [];
    const groups = planSentHistoryFetch(state.CalendarReducer?.calendarSidebarFilters, range, history);

    await Promise.all(groups.map(async group => {
        dispatch(slice.actions.started({ group, range }));
        let ok = false;
        try {
            if (group === 'messages') {
                const rows = await dispatch(DBUserMessageThunks.find(sentMessagesQuery(range) as any)).unwrap();
                ok = Array.isArray(rows);
            } else {
                const [standalone, steps] = await Promise.all([
                    fetchCampaigns(dispatch, sentCampaignsQuery(range, false)),
                    fetchCampaigns(dispatch, sentCampaignsQuery(range, true)),
                ]);
                dispatch(slice.actions.campaignsLoaded([...standalone, ...steps]));
                ok = true;
            }
        } catch (e) {
            console.warn(`[planner-sent-history] ${group} load failed for the visible range`, e);
        } finally {
            dispatch(slice.actions.settled({ group, range, ok }));
        }
    }));
    return groups;
});
